/**
 * Runtime instanced pools for the baked procedural trees (issue #58). One pool
 * per (species, structural variant); each pool is TWO InstancedMeshes sharing
 * per-instance transforms — a bark mesh (opaque MeshStandardMaterial) and a
 * foliage mesh (alpha-tested cluster cards sampling the shared foliage atlas).
 * So a pool costs exactly 2 draw calls (issue #58 AC: "<= 2 draws per (species,
 * variant, LOD) pool"), and every pool shares the same two materials + atlas
 * (issue #17: never a material or texture per tree).
 *
 * The slot model mirrors vegetation.ts's Bucket: a never-used high-water mark
 * plus a freed-index free list, zero-scale until grounded, a global live cap.
 * VegetationLayer drives claim/ground/free on the existing tile-update tick, so
 * nothing here runs per render frame beyond the instancing the renderer already
 * does (no per-frame regeneration — issue #17).
 *
 * Per-instance uniqueness (AC "no two adjacent trees look identical") comes from
 * treePoolMap.ts: structural-variant selection + scale jitter + a per-instance
 * foliage tint written to instanceColor, on top of the placement yaw.
 *
 * Attribution: tree geometry adapted from Braffolk/fable5-world-demo (MIT,
 * commit fd75fdb).
 */
import {
  BufferAttribute,
  Color,
  DoubleSide,
  Group,
  InstancedMesh,
  Matrix4,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
  type Texture,
} from 'three';
import type { BakedTrees } from './treeAssets.ts';
import { geometryFromArrays } from './treeAssets.ts';
import { makeTreeFoliageTexture } from './treeFoliageAtlas.ts';
import { scaleJitter, tintJitter } from './treePoolMap.ts';

const Y_AXIS = new Vector3(0, 1, 0);
const ZERO_SCALE = new Matrix4().makeScale(0, 0, 0);

/** Bark colour (mid brown-grey); shared by every species' trunk/branches. */
const BARK_COLOR = 0x6a5843;

export interface TreeMeshPoolsOptions {
  /** Global cap on simultaneously-live tree-mesh instances (issue #17). Also
   * the per-pool InstancedMesh capacity, since one pool may in the worst case
   * hold every live instance. */
  cap: number;
  /** InstancedMesh allocation capacity per pool, for a live quality re-cap
   * (issue #73): the active cap can be raised up to this without reallocating a
   * buffer. Defaults to cap. */
  maxCap?: number;
  /** Injected foliage atlas texture (tests pass a plain Texture; production
   * defaults to the canvas-painted atlas). */
  foliageTexture?: Texture;
}

export interface TreeSlotRef {
  key: string;
  index: number;
}

interface Pool {
  key: string;
  height: number;
  bark: InstancedMesh;
  foliage: InstancedMesh;
  nextFresh: number;
  freed: number[];
  dirty: boolean;
}

export function poolKey(speciesIndex: number, variantIndex: number): string {
  return `${speciesIndex}:${variantIndex}`;
}

export class TreeMeshPools {
  readonly group = new Group();
  /** Active cap (mutable for a live quality re-cap, issue #73). */
  private cap: number;
  /** Per-pool InstancedMesh allocation capacity (fixed); active cap ≤ it. */
  private readonly allocCap: number;
  private readonly barkMaterial: MeshStandardMaterial;
  private readonly foliageMaterial: MeshStandardMaterial;
  private readonly pools = new Map<string, Pool>();
  /** baked pool records keyed by poolKey, for lazy InstancedMesh creation. */
  private readonly baked = new Map<string, BakedTrees['pools'][number]>();
  private live = 0;
  /** derived from the baked asset so callers can map placement variants to
   * pools without importing the species table. */
  readonly speciesCount: number;
  readonly structuralVariants: number;
  private readonly _pos = new Vector3();
  private readonly _quat = new Quaternion();
  private readonly _scl = new Vector3();
  private readonly _mat = new Matrix4();
  private readonly _col = new Color();

  constructor(baked: BakedTrees, options: TreeMeshPoolsOptions) {
    this.group.name = 'tree-mesh-pools';
    this.cap = options.cap;
    this.allocCap = Math.max(options.cap, options.maxCap ?? options.cap);
    let maxSpecies = 0;
    let maxVariant = 0;
    for (const p of baked.pools) {
      this.baked.set(poolKey(p.speciesIndex, p.variantIndex), p);
      maxSpecies = Math.max(maxSpecies, p.speciesIndex);
      maxVariant = Math.max(maxVariant, p.variantIndex);
    }
    this.speciesCount = maxSpecies + 1;
    this.structuralVariants = maxVariant + 1;

    this.barkMaterial = new MeshStandardMaterial({ color: BARK_COLOR, roughness: 1, metalness: 0 });
    this.foliageMaterial = new MeshStandardMaterial({
      map: options.foliageTexture ?? makeTreeFoliageTexture(),
      alphaTest: 0.5,
      transparent: false,
      side: DoubleSide,
      roughness: 1,
      metalness: 0,
      vertexColors: true, // instanceColor tints the shared foliage atlas per tree
    });
  }

  /** Live mesh-instance count (bounded by cap). */
  get liveInstanceCount(): number {
    return this.live;
  }

  /** Global cap on live mesh instances (active; mutable via {@link setCap}). */
  get capacity(): number {
    return this.cap;
  }

  /**
   * Changes the active live-instance cap (issue #73), clamped to the allocation
   * capacity. Only sets the cap — it does not itself trim live slots; the owning
   * VegetationLayer releases and re-claims its mesh slots on a re-cap, which is
   * what actually reconciles the live count to the new cap.
   */
  setCap(cap: number): void {
    this.cap = Math.max(0, Math.min(Math.floor(cap), this.allocCap));
  }

  /** Total instanced draw calls currently submitted (meshes with count > 0). */
  get drawCallCount(): number {
    let n = 0;
    for (const p of this.pools.values()) {
      if (p.bark.count > 0) n += 1;
      if (p.foliage.count > 0) n += 1;
    }
    return n;
  }

  /** True if a pool exists in the baked asset for this (species, variant). */
  hasPool(speciesIndex: number, variantIndex: number): boolean {
    return this.baked.has(poolKey(speciesIndex, variantIndex));
  }

  private ensurePool(key: string): Pool | null {
    const existing = this.pools.get(key);
    if (existing) return existing;
    const rec = this.baked.get(key);
    if (!rec) return null;
    const barkGeom = geometryFromArrays(rec.bark);
    const foliageGeom = geometryFromArrays(rec.foliage);
    // foliageMaterial sets vertexColors: true so writeMatrix()'s per-instance
    // setColorAt() tint (issue #58's "no two adjacent trees look identical")
    // has something to multiply — without the geometry's OWN color attribute,
    // the vertex color has no valid base value and the tint multiplies the
    // atlas texture down to solid black regardless of how correct the tint or
    // texture are (issue #93). One uniform white buffer, shared by every
    // instance of this pool.
    const foliageVertexCount = foliageGeom.attributes.position.count;
    foliageGeom.setAttribute('color', new BufferAttribute(new Float32Array(foliageVertexCount * 3).fill(1), 3));
    const bark = new InstancedMesh(barkGeom, this.barkMaterial, this.allocCap);
    const foliage = new InstancedMesh(foliageGeom, this.foliageMaterial, this.allocCap);
    bark.name = `tree-bark-${key}`;
    foliage.name = `tree-foliage-${key}`;
    bark.castShadow = true;
    foliage.castShadow = false; // alpha-card shadows would read as boxy slabs
    bark.count = 0;
    foliage.count = 0;
    bark.frustumCulled = false;
    foliage.frustumCulled = false;
    this.group.add(bark, foliage);
    const pool: Pool = { key, height: rec.height, bark, foliage, nextFresh: 0, freed: [], dirty: false };
    this.pools.set(key, pool);
    return pool;
  }

  /** Claims an invisible (zero-scale) slot in a pool, or null if the global cap
   * is reached or no such pool exists. */
  claimSlot(speciesIndex: number, variantIndex: number): TreeSlotRef | null {
    if (this.live >= this.cap) return null;
    const key = poolKey(speciesIndex, variantIndex);
    const pool = this.ensurePool(key);
    if (!pool) return null;
    let index: number;
    if (pool.freed.length > 0) {
      index = pool.freed.pop() as number;
    } else if (pool.nextFresh < this.cap) {
      index = pool.nextFresh++;
      pool.bark.count = pool.nextFresh;
      pool.foliage.count = pool.nextFresh;
    } else {
      return null;
    }
    pool.bark.setMatrixAt(index, ZERO_SCALE);
    pool.foliage.setMatrixAt(index, ZERO_SCALE);
    pool.dirty = true;
    this.live += 1;
    return { key, index };
  }

  /** Frees a slot (hides its instance); reused by a later claim. */
  freeSlot(ref: TreeSlotRef): void {
    const pool = this.pools.get(ref.key);
    if (!pool) return;
    pool.bark.setMatrixAt(ref.index, ZERO_SCALE);
    pool.foliage.setMatrixAt(ref.index, ZERO_SCALE);
    pool.freed.push(ref.index);
    pool.dirty = true;
    this.live -= 1;
  }

  /** Places a claimed instance: base at ground (x,y,z), yaw about +Y, uniform
   * scale so the baked mesh's height matches desiredHeight, with deterministic
   * per-instance scale + tint jitter. */
  writeMatrix(ref: TreeSlotRef, x: number, y: number, z: number, rotationY: number, desiredHeight: number): void {
    const pool = this.pools.get(ref.key);
    if (!pool) return;
    const s = (desiredHeight / pool.height) * scaleJitter(x, z);
    this._pos.set(x, y, z);
    this._quat.setFromAxisAngle(Y_AXIS, rotationY);
    this._scl.set(s, s, s);
    this._mat.compose(this._pos, this._quat, this._scl);
    pool.bark.setMatrixAt(ref.index, this._mat);
    pool.foliage.setMatrixAt(ref.index, this._mat);
    const [tr, tg, tb] = tintJitter(x, z);
    this._col.setRGB(tr, tg, tb);
    pool.foliage.setColorAt(ref.index, this._col);
    pool.dirty = true;
  }

  /** Uploads any dirtied instance buffers to the GPU (call once per tick). */
  flush(): void {
    for (const pool of this.pools.values()) {
      if (!pool.dirty) continue;
      pool.bark.instanceMatrix.needsUpdate = true;
      pool.foliage.instanceMatrix.needsUpdate = true;
      if (pool.foliage.instanceColor) pool.foliage.instanceColor.needsUpdate = true;
      pool.dirty = false;
    }
  }
}
